import { useState } from 'react';
import { Briefcase, Car, Receipt } from 'lucide-react';
import { useHrQueue, type QueueClaim } from '../../hooks/useApproval';
import { ClaimReviewPanel } from '../../components/features/approval/ClaimReviewPanel';
import { QueueClaimNotice } from '../../components/features/approval/QueueClaimNotice';
import { useQueueClaimParam } from '../../hooks/useQueueClaimParam';
import { PageHeader, PageShell } from '@/components/PageShell';
import { ClaimTypeBadge } from '../ClaimsPage';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    year: 'numeric', month: 'long',
  });
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function HrBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-info-soft text-info border border-info/20">
      <Briefcase className="h-3 w-3" /> Finance Approved
    </span>
  );
}

// ---------------------------------------------------------------------------
// Queue card
// ---------------------------------------------------------------------------

interface HrQueueCardProps {
  claim: QueueClaim;
  onClick: () => void;
}

function HrQueueCard({ claim, onClick }: HrQueueCardProps) {
  return (
    <button
      id={`hr-card-${claim.id}`}
      onClick={onClick}
      className="w-full group text-left rounded-2xl border border-border bg-surface hover:border-primary/30 hover:shadow-md transition-all p-4 space-y-3"
    >
      {/* Top row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-info-soft flex items-center justify-center text-sm font-bold text-info shrink-0">
            {claim.user.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text truncate">{claim.user.name}</p>
            <p className="text-xs text-text-2 truncate">
              {claim.user.department ?? 'No Department'} · {claim.user.email}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-base font-bold text-text">{formatCurrency(Number(claim.totalAmount))}</p>
          <HrBadge />
        </div>
      </div>

      {/* Middle row */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div>
          <span className="text-text-2 text-xs">Period</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="font-medium text-text">{formatMonth(claim.claimMonth)}</p>
            <ClaimTypeBadge type={claim.claimType} />
          </div>
        </div>
        {claim.referenceNumber && (
          <div>
            <span className="text-text-2 text-xs">Ref</span>
            <p className="font-mono font-medium text-primary mt-0.5">{claim.referenceNumber}</p>
          </div>
        )}
        <div>
          <span className="text-text-2 text-xs">Finance Approved</span>
          <p className="font-medium text-text">{formatDate(claim.updatedAt)}</p>
        </div>
      </div>

      {claim.description && (
        <p className="text-xs text-text-2 bg-surface-2/40 rounded px-2 py-1 italic truncate">
          "{claim.description}"
        </p>
      )}

      {/* Bottom row: expense summary */}
      <div className="flex items-center justify-between pt-1 border-t border-border">
        <div className="flex gap-4 text-xs text-text-2">
          {claim.expenseSummary.mileageCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Car className="h-3.5 w-3.5 text-info" />
              {claim.expenseSummary.mileageCount} mileage · {formatCurrency(claim.expenseSummary.mileageTotal)}
            </span>
          )}
          {claim.expenseSummary.pettyCashCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Receipt className="h-3.5 w-3.5 text-info" />
              {claim.expenseSummary.pettyCashCount} petty cash · {formatCurrency(claim.expenseSummary.pettyCashTotal)}
            </span>
          )}
        </div>
        <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          HR Final Review →
        </span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function HrQueuePage() {
  const { selectedClaimId, openClaim, closeClaim } = useQueueClaimParam();
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useHrQueue({ page });
  const claims = data?.claims ?? [];
  const listedClaim = claims.find((c) => c.id === selectedClaimId) ?? null;

  // Deep link (?claim=<id>): fetch the claim directly when it is not on the current page/filter.
  const needsLookup = !!selectedClaimId && !isLoading && !listedClaim;
  const { data: linkedData, isLoading: linkedLoading } = useHrQueue(
    { claimId: selectedClaimId ?? undefined },
    { enabled: needsLookup },
  );
  const selectedClaim = listedClaim ?? linkedData?.claims.find((c) => c.id === selectedClaimId) ?? null;
  const showNotice = needsLookup && !linkedLoading && !!linkedData && !selectedClaim;

  const handleActionComplete = async () => {
    closeClaim();
    await refetch();
  };

  const dashboard = data?.dashboard;

  return (
    <PageShell>
      <PageHeader
        title="HR Review Queue"
        description="Claims approved by Finance, pending HR final clearance for payment."
      />

      {showNotice && selectedClaimId && (
        <QueueClaimNotice
          claimId={selectedClaimId}
          message="This claim is no longer awaiting your review - it may already have been actioned."
          onDismiss={closeClaim}
        />
      )}

      {/* Dashboard summary */}
      {dashboard && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-[10px] rounded-2xl border border-border bg-surface p-[20px_22px]">
            <p className="text-[13px] font-medium text-text-2">Pending Claims</p>
            <p id="hr-total-claims" className="font-mono text-[30px] font-semibold tracking-tight text-text">{dashboard.totalClaims}</p>
          </div>
          <div className="space-y-[10px] rounded-2xl border border-border bg-surface p-[20px_22px]">
            <p className="text-[13px] font-medium text-text-2">Total Pending Amount</p>
            <p id="hr-total-amount" className="font-mono text-[30px] font-semibold tracking-tight text-text">
              {new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(dashboard.totalAmountPending)}
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-2xl border border-border bg-surface-2/40 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div className="rounded-2xl border border-danger/20 bg-danger-soft p-6 text-center text-sm text-danger">
          Failed to load HR review queue. Please try refreshing.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && claims.length === 0 && (
        <div className="rounded-2xl border border-border bg-surface p-12 text-center space-y-3">
          <p className="text-base font-semibold text-text">All caught up!</p>
          <p className="text-sm text-text-2 max-w-sm mx-auto">
            There are no claims waiting for HR approval at this time.
          </p>
        </div>
      )}

      {/* List */}
      {!isLoading && claims.length > 0 && (
        <div className="space-y-3">
          {claims.map((claim) => (
            <HrQueueCard
              key={claim.id}
              claim={claim}
              onClick={() => openClaim(claim.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.meta.pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg border border-border text-sm font-medium text-text hover:bg-surface-2 disabled:opacity-50 transition-colors"
          >
            ← Prev
          </button>
          <span className="text-sm text-text-2">
            Page {page} of {data.meta.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.meta.pagination.totalPages, p + 1))}
            disabled={page === data.meta.pagination.totalPages}
            className="px-3 py-1.5 rounded-lg border border-border text-sm font-medium text-text hover:bg-surface-2 disabled:opacity-50 transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {/* Review panel */}
      {selectedClaim && (
        <ClaimReviewPanel
          claim={selectedClaim}
          mode="hr"
          onClose={closeClaim}
          onActionComplete={handleActionComplete}
        />
      )}
    </PageShell>
  );
}
